import { useCallback, useEffect, useRef } from "react";
import { t } from "i18next";
import axios from "axios";
import { useUserData } from "../../../hooks/useUserData";
import { useAccountData } from "../../../hooks/useAccountData";
import { pendingAuthPaymentConfigStore } from "../../../hookstate/pendingAuthPaymentConfigStore";
import { FunnelOnSuccess } from "../../funnel/types";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { SubscriptionOfferV2Type } from "../types";
import {
  createWebSubscriptionFromSavedPaymentMethod,
  createWebSubscriptionFromSetupSession,
  isBundlePaymentSetupPayload,
  isReleasePaymentSetupPayload,
  isWebPurchasePayload,
  isWebSubscriptionCheckoutPayload,
  WebSubscriptionCheckoutSessionPayload,
} from "../api/paymentApi";
import {
  PendingStripeSubscriptionSetupIntent,
  useStripeCheckoutSubscriptionSetup,
} from "./useStripeCheckoutSubscriptionSetup";
import {
  clearPendingGuestStripeSubscriptionSetup,
  getPendingGuestStripeSubscriptionSetup,
  markPendingGuestStripeSubscriptionSetupIntentCompleted,
  updatePendingGuestStripeSubscriptionSetupPayload,
} from "../utils/pendingGuestStripeSubscriptionSetup";
import { useRefetchQueries } from "../../../services/axiosInstance";
import { ACCOUNT_INIT_QUERY_KEY } from "../../../appStore";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../../auth/api/authApi";
import {
  refetchStripeReturnUntilSubscriptionVisible,
  StripeReturnPollingTimeoutError,
} from "./refetchStripeSubscriptionReturn";
import {
  clearSubscriptionStripePaymentSettling,
  markSubscriptionStripePaymentSettling,
} from "../utils/stripePaymentSettlingStore";

type UseSubscriptionPurchaseV2Args = {
  activeType: "yearly" | "monthly" | undefined;
  yearlySub: SubscriptionOfferV2Type | undefined;
  monthlySub: SubscriptionOfferV2Type | undefined;
  promoCode: string;
  setFunnelType: (type: FUNNEL_TYPES) => void;
  welcomeGiftId?: number;
  hasSavedPaymentMethod?: boolean;
  onFunnelSuccess?: FunnelOnSuccess;
  onPurchaseError?: () => void;
};

export function useSubscriptionPurchaseV2({
  activeType,
  yearlySub,
  monthlySub,
  promoCode,
  setFunnelType,
  welcomeGiftId,
  hasSavedPaymentMethod = false,
  onFunnelSuccess,
  onPurchaseError,
}: UseSubscriptionPurchaseV2Args) {
  const { userData } = useUserData();
  const accountData = useAccountData();
  const preservePendingCheckoutOnUnmountRef = useRef(false);
  const handledPendingGuestSetupRef = useRef(false);
  const {
    startSetupCheckout,
    waiting: setupWaiting,
    error: setupError,
    setError: setSetupError,
    setupIntent,
    clearSetupIntent,
  } = useStripeCheckoutSubscriptionSetup();
  // Refetch the same queries the hosted-Checkout return path refreshes so
  // gold-driven UI updates land at the same time on both code paths.
  const refetchAccountAfterSubscribe = useRefetchQueries(
    ACCOUNT_INIT_QUERY_KEY,
    GET_ACCOUNT_ME_QUERY_KEY,
  );

  // Poll account-me until the new subscription is visible. The banner is
  // marked/cleared by the caller so it covers the upstream API call too —
  // otherwise users see no feedback while web-subscription-from-* is in
  // flight (the OPTIONS preflight + POST take a couple of seconds).
  const pollUntilSubscriptionVisible = useCallback(async () => {
    try {
      await refetchStripeReturnUntilSubscriptionVisible(
        refetchAccountAfterSubscribe,
        ([nextAccountData]) =>
          Boolean(
            (nextAccountData as { data?: { payload?: { results?: { subscription?: boolean; user?: { gold?: boolean } } } } })
              ?.data?.payload?.results?.subscription ||
            (nextAccountData as { data?: { payload?: { results?: { user?: { gold?: boolean } } } } })
              ?.data?.payload?.results?.user?.gold,
          ),
      );
    } catch (error) {
      if (!(error instanceof StripeReturnPollingTimeoutError)) {
        throw error;
      }
      // Polling timeout is non-fatal — the natural refetch will eventually
      // catch up. The caller's finally still clears the banner.
      console.warn(
        "[useSubscriptionPurchaseV2] Subscription not yet visible after polling",
        error,
      );
    }
  }, [refetchAccountAfterSubscribe]);

  const getCheckoutPayload =
    useCallback((): WebSubscriptionCheckoutSessionPayload => {
      const { access_token_type_id, promo_code } =
        (activeType === "yearly" ? yearlySub : monthlySub) ?? {};

      return {
        accessTokenTypeId: access_token_type_id ?? 0,
        incentiveCode: promoCode.length ? promoCode : promo_code,
        userOfferUuid: accountData?.user_offer || undefined,
        context: "subscription_modal_v2",
        // welcome_gift_id is yearly-only. Even if a stale id is hanging
        // around in caller state, never let it reach the monthly submit —
        // the backend rejects that combination with 400 (GX-3877).
        welcomeGiftId: activeType === "yearly" ? welcomeGiftId : undefined,
      };
    }, [
      accountData?.user_offer,
      activeType,
      monthlySub,
      promoCode,
      welcomeGiftId,
      yearlySub,
    ]);

  const getErrorMessage = useCallback(
    (checkoutError: Error) => {
      onPurchaseError?.();

      let errorMessage = t(
        "components.payments.subscription.subscriptionDeclined",
      );

      if (axios.isAxiosError<{ payload?: { results?: string } }>(checkoutError)) {
        errorMessage =
          checkoutError.response?.data?.payload?.results || errorMessage;
      }

      return errorMessage;
    },
    [onPurchaseError],
  );

  const submitPurchase = useCallback(async () => {
    const checkoutPayload = getCheckoutPayload();

    if (!userData) {
      try {
        await startSetupCheckout(checkoutPayload);
        return;
      } catch (checkoutError) {
        const errorMessage = getErrorMessage(checkoutError as Error);
        setSetupError(errorMessage);
        return;
      }
    }

    try {
      preservePendingCheckoutOnUnmountRef.current = false;
      if (hasSavedPaymentMethod) {
        markSubscriptionStripePaymentSettling();
        try {
          await createWebSubscriptionFromSavedPaymentMethod(checkoutPayload);
          await pollUntilSubscriptionVisible();
          onFunnelSuccess?.({
            data: [null, null],
            options: {
              didPurchaseSubscription: true,
              purchasedYearly: activeType === "yearly",
            },
          });
        } finally {
          clearSubscriptionStripePaymentSettling();
        }
        return;
      }

      await startSetupCheckout(checkoutPayload);
    } catch (checkoutError) {
      if (hasSavedPaymentMethod) {
        try {
          await startSetupCheckout(checkoutPayload);
          return;
        } catch (setupError) {
          const errorMessage = getErrorMessage(setupError as Error);
          setSetupError(errorMessage);
          return;
        }
      }

      const errorMessage = getErrorMessage(checkoutError as Error);
      setSetupError(errorMessage);
    }
  }, [
    activeType,
    pollUntilSubscriptionVisible,
    getCheckoutPayload,
    getErrorMessage,
    hasSavedPaymentMethod,
    onFunnelSuccess,
    startSetupCheckout,
    setSetupError,
    userData,
  ]);

  const completeGuestSetup = useCallback(
    async (setupIntentId: string, clientNonce?: string) => {
      updatePendingGuestStripeSubscriptionSetupPayload(getCheckoutPayload());
      markPendingGuestStripeSubscriptionSetupIntentCompleted(setupIntentId);
      clearSetupIntent();
      if (userData) {
        markSubscriptionStripePaymentSettling();
        try {
          await createWebSubscriptionFromSetupSession({
            ...getCheckoutPayload(),
            setupIntentId,
            clientNonce,
          });
          clearPendingGuestStripeSubscriptionSetup();
          await pollUntilSubscriptionVisible();
          onFunnelSuccess?.({
            data: [null, null],
            options: {
              didPurchaseSubscription: true,
              purchasedYearly: activeType === "yearly",
            },
          });
        } finally {
          clearSubscriptionStripePaymentSettling();
        }
        return;
      }

      setFunnelType(FUNNEL_TYPES.signup);
    },
    [
      activeType,
      clearSetupIntent,
      pollUntilSubscriptionVisible,
      getCheckoutPayload,
      onFunnelSuccess,
      setFunnelType,
      userData,
    ],
  );

  useEffect(() => {
    if (handledPendingGuestSetupRef.current || !userData) {
      return;
    }

    const pendingGuestSetup = getPendingGuestStripeSubscriptionSetup();
    if (!pendingGuestSetup?.completedSetupIntentId) {
      return;
    }

    handledPendingGuestSetupRef.current = true;
    preservePendingCheckoutOnUnmountRef.current = false;

    if (userData.gold) {
      clearPendingGuestStripeSubscriptionSetup();
      onFunnelSuccess?.({
        data: [null, null],
        options: { didPurchaseSubscription: false },
      });
      return;
    }

    markSubscriptionStripePaymentSettling();
    void createWebSubscriptionFromSetupSession({
      ...pendingGuestSetup.payload,
      setupIntentId: pendingGuestSetup.completedSetupIntentId,
      clientNonce: pendingGuestSetup.clientNonce,
    })
      .then(async () => {
        try {
          clearPendingGuestStripeSubscriptionSetup();
          await pollUntilSubscriptionVisible();
          onFunnelSuccess?.({
            data: [null, null],
            options: { didPurchaseSubscription: true },
          });
        } finally {
          clearSubscriptionStripePaymentSettling();
        }
      })
      .catch((checkoutError) => {
        handledPendingGuestSetupRef.current = false;
        clearSubscriptionStripePaymentSettling();
        setSetupError(getErrorMessage(checkoutError as Error));
      });
  }, [
    getErrorMessage,
    onFunnelSuccess,
    pollUntilSubscriptionVisible,
    setSetupError,
    userData,
  ]);

  useEffect(() => {
    const pendingConfig = pendingAuthPaymentConfigStore.get({
      noproxy: true,
    });

    if (
      !pendingConfig ||
      isWebPurchasePayload(pendingConfig) ||
      isReleasePaymentSetupPayload(pendingConfig) ||
      isBundlePaymentSetupPayload(pendingConfig) ||
      !isWebSubscriptionCheckoutPayload(pendingConfig) ||
      !userData
    ) {
      return;
    }

    if (userData.gold) {
      preservePendingCheckoutOnUnmountRef.current = false;
      pendingAuthPaymentConfigStore.set(undefined);
      onFunnelSuccess?.({
        data: [null, null],
        options: { didPurchaseSubscription: false },
      });
      return;
    }

    preservePendingCheckoutOnUnmountRef.current = false;
    if (hasSavedPaymentMethod) {
      markSubscriptionStripePaymentSettling();
      void createWebSubscriptionFromSavedPaymentMethod(pendingConfig)
        .then(async () => {
          try {
            await pollUntilSubscriptionVisible();
            onFunnelSuccess?.({
              data: [null, null],
              options: { didPurchaseSubscription: true },
            });
          } finally {
            clearSubscriptionStripePaymentSettling();
          }
        })
        .catch((checkoutError) => {
          clearSubscriptionStripePaymentSettling();
          setSetupError(getErrorMessage(checkoutError as Error));
        });
    } else {
      void startSetupCheckout(pendingConfig).catch((checkoutError) => {
        setSetupError(getErrorMessage(checkoutError as Error));
      });
    }
    pendingAuthPaymentConfigStore.set(undefined);
  }, [
    pollUntilSubscriptionVisible,
    getErrorMessage,
    hasSavedPaymentMethod,
    onFunnelSuccess,
    setSetupError,
    startSetupCheckout,
    userData,
  ]);

  useEffect(() => {
    return () => {
      if (preservePendingCheckoutOnUnmountRef.current) {
        return;
      }

      pendingAuthPaymentConfigStore.set(undefined);
    };
  }, []);

  return {
    submitPurchase,
    waiting: setupWaiting,
    checkoutError: setupError,
    guestSetupIntent: setupIntent as
      | PendingStripeSubscriptionSetupIntent
      | undefined,
    completeGuestSetup,
    clearGuestSetup: clearSetupIntent,
  };
}
